import { useFormatters } from '@/composables/useFormatters'
import type { AnyCalculatorDefinition, FieldDefinition } from '@/types/calculator'

export interface ResultEmail {
  subject: string
  body: string
}

function formatInputValue(field: FieldDefinition, value: unknown, currency: string): string {
  const { formatCurrency, formatPercent, formatNumber } = useFormatters()
  switch (field.type) {
    case 'money':
      return formatCurrency(Number(value), currency)
    case 'percentage':
      return formatPercent(Number(value))
    case 'number': {
      const num = Number(value)
      const formatted = formatNumber(num, Number.isInteger(num) ? 0 : 2)
      return field.unit && field.unit !== 'R' ? `${formatted} ${field.unit}` : formatted
    }
    case 'select':
    case 'radio':
      return field.options?.find((o) => o.value === value)?.label ?? String(value)
    case 'checkbox':
      return value ? 'Yes' : 'No'
    case 'text':
    default:
      return String(value)
  }
}

function formatResultValue(
  format: 'currency' | 'percent' | 'number' | 'plain' | undefined,
  value: number | string,
  currency: string
): string {
  const { formatCurrency, formatPercent, formatNumber } = useFormatters()
  if (typeof value !== 'number') return String(value)
  switch (format) {
    case 'currency':
      return formatCurrency(value, currency)
    case 'percent':
      return formatPercent(value)
    case 'number':
      return formatNumber(value, Number.isInteger(value) ? 0 : 2)
    default:
      return String(value)
  }
}

/**
 * Build a plain-text email summarising a calculator result. mailto: links
 * cannot carry HTML, so the body is simple line-based text.
 */
export function buildResultEmail(args: {
  definition: AnyCalculatorDefinition
  inputs: Record<string, unknown>
  result: Record<string, unknown>
  shareUrl: string
  siteName: string
  currency: string
  taxYearLabel: string
}): ResultEmail {
  const { definition, inputs, result, shareUrl, siteName, currency, taxYearLabel } = args

  const inputLines = definition.inputs
    .filter((field) => {
      const value = inputs[field.name]
      return value !== undefined && value !== null && value !== ''
    })
    .map((field) => `- ${field.label}: ${formatInputValue(field, inputs[field.name], currency)}`)

  const highlighted = definition.resultSections.filter((s) => s.highlighted)
  const rest = definition.resultSections.filter((s) => !s.highlighted)
  const resultLines = [
    ...highlighted.map(
      (s) => `${s.label}: ${formatResultValue(s.format, s.value(result), currency)}`
    ),
    ...rest.map((s) => `- ${s.label}: ${formatResultValue(s.format, s.value(result), currency)}`),
  ]

  const sourceLines = definition.assumptions.map(
    (a) =>
      `- ${a.name} (effective ${a.effectiveFrom}${a.effectiveTo ? ` to ${a.effectiveTo}` : ''})${a.url ? ` — ${a.url}` : ''}`
  )

  const subject = `${definition.shortTitle ?? definition.title} result — ${siteName}`

  const body = [
    `${definition.title}\n${siteName} · Tax year ${taxYearLabel}`,
    `YOUR INPUTS\n${inputLines.join('\n')}`,
    `RESULTS\n${resultLines.join('\n')}`,
    `Open this calculation with your inputs pre-filled:\n${shareUrl}`,
    `SOURCES\n${sourceLines.join('\n')}`,
    `This is an estimate only and does not constitute financial, tax, legal or lending advice.\nGenerated by ${siteName}.`,
  ].join('\n\n')

  return { subject, body }
}

export function mailtoUrl(email: ResultEmail): string {
  return `mailto:?subject=${encodeURIComponent(email.subject)}&body=${encodeURIComponent(email.body)}`
}

/**
 * Open the user's mail client with the given mailto: URL. Extracted so tests
 * can spy on the navigation, which is awkward to intercept in jsdom.
 */
export function openMailto(url: string): void {
  window.location.href = url
}
